import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/apiClient';
import { useToast } from '../hooks/useToast';
import type { BrandCutGenerationParams } from '../services/asyncJobApi';
import {
  VideoGenerationSettings,
  useEstimatedVideoCredits,
} from './generation/scenario/VideoGenerationSettings';
import { DEFAULT_VIDEO_SETTINGS, type VideoSettings } from './generation/state/useGenerationState';
import { GenerateButton } from './generation/common/GenerateButton';
import LogoManagementModal from './LogoManagementModal';
import { getLogoAttributeLabels, logoCardBackground } from '../features/brands/logoAttributes';
import { Tooltip } from './common/Tooltip';
import { useGenerationModels } from '../features/generation-models/hooks';
import { useCreditRates } from '../features/organizations/hooks';
import { MotionModelSelect } from './generation/common/MotionModelSelect';
import { MotionQualitySelect } from './generation/common/MotionQualitySelect';
import { currentMotionModel } from '../features/motion/model';
import { currentMotionQuality } from '../features/motion/quality';
import { BRAND_COLOR_FALLBACK_NOTICE, isBrandColorUnset, useBrandDetail } from '../features/brands/designLanguage';
import styles from './BrandCutModal.module.css';
import '../styles/buttons.css';

// ブランドカット既定モデルは Seedance 2（標準）。一覧に無ければ
// VideoGenerationSettings 側で DB 既定にフォールバックする。
const INITIAL_VIDEO_SETTINGS: VideoSettings = {
  ...DEFAULT_VIDEO_SETTINGS,
  videoModel: 'seedance-2-standard',
  // generateAudio は DEFAULT_VIDEO_SETTINGS の既定 (音声あり) を継承。
};

/** 作り方 (docs/features/brand-cut-motion-graphics-plan.md §8.1)。 */
type BrandCutEngine = 'video' | 'motion';
/** モーショングラフィックスの尺 (2〜16 秒) とアスペクト比 (adaptive は無い)。backend の検証と同じ範囲。 */
const MOTION_DURATION_OPTIONS = [2, 3, 4, 5, 6, 8, 10, 12, 16] as const;
const MOTION_ASPECT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '16:9', label: '16:9（横）' },
  { value: '9:16', label: '9:16（縦）' },
  { value: '1:1', label: '1:1（正方形）' },
  { value: '4:3', label: '4:3' },
  { value: '3:4', label: '3:4' },
  { value: '21:9', label: '21:9（シネマ）' },
];
const MOTION_MAX_LOGOS = 9;
/** credit_rate_config の定額 (backend/services/brand_cut_motion/billing.py と同じキー) */
const MOTION_RATE_SERVICE_TYPE = 'video_generation';
const MOTION_RATE_RESOURCE_TYPE = 'motion-graphics-brand-cut';

export interface BrandCutModalLogo {
  id: string; // brand_logos.id
  description?: string;
  background?: string; // white_back / black_back / transparent
  color_type?: string; // full_color / monochrome / white 等
  layout?: string; // horizontal / vertical / symbol 等
  is_default?: boolean;
  logos?: {
    url?: string;
    file_name?: string;
  };
}

interface BrandCutModalProps {
  isOpen: boolean;
  onClose: () => void;
  brandId: string;
  brandName: string;
  tagline?: string;
  description?: string;
  brandMainColor?: string;
  brandSubColor?: string;
  brandAccentColor?: string;
  logos: BrandCutModalLogo[];
  /** 生成ジョブの投入は親に委譲（グローバルキューに積んで下部ステータスバーに表示する） */
  onSubmit: (params: BrandCutGenerationParams) => void;
  /** 作り方の既定 (全編モーションのシナリオから開くときは 'motion') */
  defaultEngine?: BrandCutEngine;
  /** モーションのアスペクト比の既定 (シナリオの比率) */
  defaultAspectRatio?: string;
  /** 全編モーションの計画の尺 (秒・小数可)。あればモーションの尺をこの値に固定する (§10.5) */
  plannedMotionSeconds?: number | null;
  /** シナリオのブランドカットのシーン id (モーションのときだけ送る。計画の版を成果物に記録する) */
  sceneId?: string;
}

/**
 * ブランドカット動画（ロゴアニメーション）生成モーダル。2カラム構成。
 * 左: ロゴ選択 / ブランドカラー / 動画モデル設定（スタジオと同じ UI）
 * 右: ブランド名 / スローガン / 説明 / イメージ（いずれもこの生成用に編集可）
 * 生成物はライブラリに「ブランドカット」タグ + ブランド紐付きで保存される。
 */
export default function BrandCutModal({
  isOpen,
  onClose,
  brandId,
  brandName,
  tagline,
  description,
  brandMainColor,
  brandSubColor,
  brandAccentColor,
  logos,
  onSubmit,
  defaultEngine,
  defaultAspectRatio,
  plannedMotionSeconds = null,
  sceneId,
}: BrandCutModalProps) {
  const toast = useToast();

  const [selectedLogoIds, setSelectedLogoIds] = useState<string[]>([]);
  const [nameInput, setNameInput] = useState('');
  const [taglineInput, setTaglineInput] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');
  const [imageText, setImageText] = useState('');
  const [mainColor, setMainColor] = useState('');
  const [subColor, setSubColor] = useState('');
  const [accentColor, setAccentColor] = useState('');
  const [videoSettings, setVideoSettings] = useState<VideoSettings>(INITIAL_VIDEO_SETTINGS);
  const [engine, setEngine] = useState<BrandCutEngine>('video');
  const [motionDuration, setMotionDuration] = useState<number>(4);
  const [motionAspect, setMotionAspect] = useState<string>('16:9');
  // 効果音 (計画 md §14): なし / おまかせ。サウンドロゴはブランドに登録があるときだけ選べる
  const [soundEffects, setSoundEffects] = useState<'none' | 'auto'>('none');
  const [soundLogoId, setSoundLogoId] = useState<string>('');
  const { data: soundLogosData } = useQuery({
    queryKey: ['brand-sound-logos', brandId],
    queryFn: async () => {
      const resp = await apiClient.get<{ sound_logos: Array<{ material_id: string; file_name: string | null; name: string | null; duration_seconds: number | null }> }>(`/brands/${brandId}/sound-logos`);
      return resp.data.sound_logos;
    },
    enabled: isOpen && engine === 'motion' && !!brandId,
  });
  const soundLogos = soundLogosData ?? [];
  // デザイン言語の有無 (§10.10.4)。色が全て空でデザイン言語も無ければ backend はロゴから色を推定するので注意を出す
  const { data: brandDetail } = useBrandDetail(brandId, isOpen && engine === 'motion');
  // ロゴ未登録時に開く新規登録モーダル。登録すると brand クエリが invalidate され logos が更新される
  const [logoModalOpen, setLogoModalOpen] = useState(false);

  const selectableLogos = useMemo(
    () => logos.filter((l) => l.logos?.url),
    [logos]
  );
  // モデル別の参照画像（ロゴ）上限。DB (generation_models.metadata.max_reference_images) を
  // 単一の情報源にする (旧実装は Seedance=9/それ以外=3 のプロバイダ名ハードコードで、新モデル
  // 追加のたびに更新漏れが起きていた。MiniMax H3 の上限 5 もこの経路で自動的に反映される)。
  // metadata キーが無いモデルのみ既定値 3 (Veo 系) にフォールバックする。
  const { data: videoModelsForLogoLimit = [] } = useGenerationModels('video');
  const maxLogos = useMemo(() => {
    if (engine === 'motion') return MOTION_MAX_LOGOS;
    const fromMeta = (videoModelsForLogoLimit.find((m) => m.model_code === videoSettings.videoModel)
      ?.metadata as { max_reference_images?: number } | null | undefined)?.max_reference_images;
    return typeof fromMeta === 'number' ? fromMeta : 3;
  }, [engine, videoModelsForLogoLimit, videoSettings.videoModel]);
  // 消費見積もりポイント (ロゴ=参照画像モード。Veo の 8 秒固定もフック内で考慮)
  const estimatedCredits = useEstimatedVideoCredits(videoSettings, true);
  // モーショングラフィックスは定額 (credit_rate_config)。未設定なら backend が 503 で受け付けないので送らせない
  const { data: creditRatesData } = useCreditRates();
  const motionRate = useMemo(() => {
    // useCreditRates は { rates: [...] } を返す (useEstimatedVideoCredits と同じ参照の仕方)
    const rate = creditRatesData?.rates?.find(
      (r) => r.service_type === MOTION_RATE_SERVICE_TYPE && r.resource_type === MOTION_RATE_RESOURCE_TYPE,
    );
    return rate ? rate.credits_per_unit : null;
  }, [creditRatesData]);
  const creditCost = engine === 'motion' ? Math.floor(motionRate ?? 0) : Math.floor(estimatedCredits);
  const motionRateMissing = engine === 'motion' && motionRate === null;
  // 送る色 (この画面で編集した値) が全て空・かつブランドにデザイン言語が無い
  const colorFallback = engine === 'motion' && !!brandDetail && isBrandColorUnset({
    brand_main_color: mainColor,
    brand_sub_color: subColor,
    brand_accent_color: accentColor,
    design_language: brandDetail.design_language,
  });

  // モーダルを開くたびに初期値をブランド値で埋め直す
  useEffect(() => {
    if (isOpen) {
      setSelectedLogoIds([]);
      setNameInput(brandName ?? '');
      setTaglineInput(tagline ?? '');
      setDescriptionInput(description ?? '');
      setImageText('');
      setMainColor(brandMainColor ?? '');
      setSubColor(brandSubColor ?? '');
      setAccentColor(brandAccentColor ?? '');
      setVideoSettings(INITIAL_VIDEO_SETTINGS);
      setEngine(defaultEngine ?? 'video');
      setMotionDuration(4);
      setMotionAspect(defaultAspectRatio && MOTION_ASPECT_OPTIONS.some((o) => o.value === defaultAspectRatio) ? defaultAspectRatio : '16:9');
    }
  }, [isOpen, brandName, tagline, description, brandMainColor, brandSubColor, brandAccentColor, defaultEngine, defaultAspectRatio]);

  // モデル変更で上限が下がった場合は選択を上限まで絞る
  useEffect(() => {
    setSelectedLogoIds((prev) => (prev.length > maxLogos ? prev.slice(0, maxLogos) : prev));
  }, [maxLogos]);

  // モーダル表示中は背景スクロールを無効化
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const toggleLogo = (logoId: string) => {
    setSelectedLogoIds((prev) => {
      if (prev.includes(logoId)) {
        return prev.filter((x) => x !== logoId);
      }
      if (prev.length >= maxLogos) {
        toast.error(engine === 'motion' ? `モーショングラフィックスではロゴは最大 ${maxLogos} 枚まで選択できます` : `このモデルではロゴは最大 ${maxLogos} 枚まで選択できます`);
        return prev;
      }
      return [...prev, logoId];
    });
  };

  const colorSlots: Array<{ key: string; label: string; value: string; set: (v: string) => void }> = [
    { key: 'main', label: 'メインカラー', value: mainColor, set: setMainColor },
    { key: 'sub', label: 'サブカラー', value: subColor, set: setSubColor },
    { key: 'accent', label: 'アクセントカラー', value: accentColor, set: setAccentColor },
  ];

  const handleSubmit = () => {
    if (selectedLogoIds.length === 0) {
      toast.error('ロゴを1つ以上選択してください');
      return;
    }
    if (!nameInput.trim()) {
      toast.error('ブランド名を入力してください');
      return;
    }
    if (motionRateMissing) {
      toast.error('モーショングラフィックスの料金が設定されていないため、現在ご利用いただけません');
      return;
    }
    // 生成ジョブの投入は親に委譲（グローバルキューに積んで下部に進捗表示）。
    const common = {
      brand_id: brandId,
      logo_ids: selectedLogoIds,
      // ブランド名/スローガン/説明は常に送る (空文字 = クリア)。ブランド値にフォールバックさせない
      brand_name: nameInput.trim(),
      image_text: imageText.trim() || undefined,
      tagline: taglineInput.trim(),
      description: descriptionInput.trim(),
      // 色は常に送る (空文字 = クリア)。送ればその値で上書きされる
      brand_main_color: mainColor.trim(),
      brand_sub_color: subColor.trim(),
      brand_accent_color: accentColor.trim(),
    };
    if (engine === 'motion') {
      // モーショングラフィックス: 動画モデル・解像度・音声は送らない (backend も params に載せない)
      onSubmit({
        ...common,
        engine: 'motion',
        duration_seconds: plannedMotionSeconds ?? motionDuration,
        ...(sceneId ? { scene_id: sceneId } : {}),
        aspect_ratio: motionAspect,
        sound_effects: soundEffects,
        ...(soundLogoId ? { sound_logo_material_id: soundLogoId } : {}),
        quality: currentMotionQuality('brandCut'),
        model: currentMotionModel(),
      });
    } else {
      onSubmit({
        ...common,
        engine: 'video',
        duration_seconds: videoSettings.duration,
        video_model: videoSettings.videoModel,
        resolution: videoSettings.resolution,
        aspect_ratio: videoSettings.aspectRatio,
        generate_audio: videoSettings.generateAudio,
      });
    }
    onClose();
  };

  return createPortal(
    <>
    {/* 入力内容の消失を防ぐため、オーバーレイのクリックでは閉じない (✕ ボタンのみ) */}
    <div className="modal-overlay modal-overlay--nested">
      <div className={`modal-card ${styles.fullCard}`}>
        <div className="modal-header">
          <h2 className="modal-title">ブランドカット動画を生成</h2>
          <button
            type="button"
            className="modal-close-button"
            onClick={onClose}
            aria-label="閉じる"
          >
            ×
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.columns}>
            {/* 左カラム: ロゴ選択 / ブランドカラー / 動画モデル設定 */}
            <div className={styles.col}>
              {/* ロゴ選択 */}
              <div className={styles.field}>
                <h3 className={styles.sectionHeading}>ロゴ<span className={styles.required}>*</span></h3>
                <span className={styles.fieldHint}>基本的に1つだけ選択してください</span>
                {selectableLogos.length > 0 ? (
                  <div className={styles.logoGrid}>
                    {selectableLogos.map((logo) => {
                      const selected = selectedLogoIds.includes(logo.id);
                      // 透過/非透過を見分けられるよう、地色は単色ではなく市松模様にする。
                      // トーンは既存規約 logoCardBackground (透過×白抜き→暗 / それ以外→明) を踏襲。
                      const checkerClass =
                        logoCardBackground(logo) === '#000000'
                          ? styles.logoCardCheckerDark
                          : styles.logoCardCheckerLight;
                      const attributeLabels = getLogoAttributeLabels(logo);
                      const fileName = logo.logos?.file_name;
                      return (
                        <Tooltip
                          key={logo.id}
                          className={styles.logoCardWrap}
                          content={
                            <div className={styles.logoTooltip}>
                              <div className={styles.logoTooltipTitle}>
                                {logo.description || '説明なし'}
                                {logo.is_default && (
                                  <span className={styles.logoTooltipBadge}>既定</span>
                                )}
                              </div>
                              {attributeLabels.length > 0 && (
                                <div className={styles.logoTooltipRow}>{attributeLabels.join(' / ')}</div>
                              )}
                              {fileName && (
                                <div className={styles.logoTooltipFile}>{fileName}</div>
                              )}
                            </div>
                          }
                        >
                          <button
                            type="button"
                            className={`${styles.logoCard} ${checkerClass} ${selected ? styles.logoCardSelected : ''}`}
                            onClick={() => toggleLogo(logo.id)}
                          >
                            <img
                              src={logo.logos?.url}
                              alt={logo.description || 'ロゴ'}
                              className={styles.logoCardImg}
                            />
                            {selected && <span className={styles.logoCheck}>✓</span>}
                          </button>
                        </Tooltip>
                      );
                    })}
                  </div>
                ) : (
                  <span className={styles.fieldHint}>
                    まずは
                    <button
                      type="button"
                      className={styles.inlineLink}
                      onClick={() => setLogoModalOpen(true)}
                    >
                      ロゴを登録
                    </button>
                    してください。
                  </span>
                )}
              </div>

              {/* ブランドカラー（編集可・カラーピッカー + クリア） */}
              <div className={styles.field}>
                <h3 className={styles.sectionHeading}>カラー</h3>
                <div className={styles.colorList}>
                  {colorSlots.map((slot) => (
                    <div key={slot.key} className={styles.colorField}>
                      <span className={styles.colorLabel}>{slot.label}</span>
                      <div className={styles.colorEditRow}>
                        <input
                          type="color"
                          className={styles.colorPicker}
                          value={/^#[0-9a-fA-F]{6}$/.test(slot.value) ? slot.value : '#000000'}
                          onChange={(e) => slot.set(e.target.value)}
                          aria-label={`${slot.label}を選択`}
                        />
                        <input
                          type="text"
                          className={styles.colorHex}
                          value={slot.value}
                          onChange={(e) => slot.set(e.target.value)}
                          placeholder="未設定"
                        />
                        {slot.value && (
                          <button
                            type="button"
                            className={styles.colorClear}
                            onClick={() => slot.set('')}
                            title="クリア"
                            aria-label={`${slot.label}をクリア`}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 作り方: 動画生成 AI (従来) / モーショングラフィックス (Claude が Remotion コードを書いて描く) */}
              <div className={styles.field}>
                <h3 className={styles.sectionHeading}>作り方</h3>
                <div className={styles.engineSwitch} role="radiogroup" aria-label="作り方">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={engine === 'video'}
                    className={`${styles.engineOption} ${engine === 'video' ? styles.engineOptionActive : ''}`}
                    onClick={() => setEngine('video')}
                  >
                    <span className={styles.engineOptionTitle}>動画生成 AI</span>
                    <span className={styles.engineOptionDesc}>ロゴ画像を参照して動画モデルが生成。実写・質感のある演出向け</span>
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={engine === 'motion'}
                    className={`${styles.engineOption} ${engine === 'motion' ? styles.engineOptionActive : ''}`}
                    onClick={() => setEngine('motion')}
                  >
                    <span className={styles.engineOptionTitle}>モーショングラフィックス</span>
                    <span className={styles.engineOptionDesc}>ロゴ・文字・ブランドカラーを正確に再現するアニメーション。定額</span>
                  </button>
                </div>
              </div>

              {engine === 'motion' ? (
                <div className={styles.field}>
                  <h3 className={styles.sectionHeading}>モーション設定</h3>
                  <div className={styles.motionSettings}>
                    <label className={styles.motionSetting}>
                      <span className={styles.fieldLabel}>尺</span>
                      {plannedMotionSeconds != null ? (
                        // 全編モーションの計画の尺 (小節頭の実時刻・§10.5)。変えると計画と合わなくなるので固定
                        <span className={styles.input} style={{ display: 'inline-block' }} title="BGM の小節に合わせた計画の尺です">
                          {plannedMotionSeconds} 秒 (計画)
                        </span>
                      ) : (
                        <select
                          className={styles.input}
                          value={motionDuration}
                          onChange={(e) => setMotionDuration(Number(e.target.value))}
                        >
                          {MOTION_DURATION_OPTIONS.map((sec) => (
                            <option key={sec} value={sec}>{sec} 秒</option>
                          ))}
                        </select>
                      )}
                    </label>
                    <label className={styles.motionSetting}>
                      <span className={styles.fieldLabel}>アスペクト比</span>
                      <select
                        className={styles.input}
                        value={motionAspect}
                        onChange={(e) => setMotionAspect(e.target.value)}
                      >
                        {MOTION_ASPECT_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className={styles.motionSettings}>
                    <label className={styles.motionSetting}>
                      <span className={styles.fieldLabel}>効果音</span>
                      <select
                        className={styles.input}
                        value={soundEffects}
                        onChange={(e) => setSoundEffects(e.target.value as 'none' | 'auto')}
                      >
                        <option value="none">なし</option>
                        <option value="auto">おまかせ（演出の節目に AI が置く）</option>
                      </select>
                    </label>
                    {soundLogos.length > 0 && (
                      <label className={styles.motionSetting}>
                        <span className={styles.fieldLabel}>サウンドロゴ</span>
                        <select
                          className={styles.input}
                          value={soundLogoId}
                          onChange={(e) => setSoundLogoId(e.target.value)}
                        >
                          <option value="">鳴らさない</option>
                          {soundLogos.map((s) => (
                            <option key={s.material_id} value={s.material_id}>
                              {s.name || s.file_name || 'サウンドロゴ'}{s.duration_seconds ? `（${Math.round(s.duration_seconds * 10) / 10} 秒）` : ''}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                  </div>
                  <p className={styles.fieldHint}>
                    最後の 0.5 秒はロゴが静止して残ります（エンドカードとしても使えます）。
                    {soundEffects === 'auto' || soundLogoId
                      ? ' 効果音の音源はブランドの雰囲気に合わせて都度生成し、サウンドロゴはロゴが決まる瞬間に鳴らします。'
                      : ' 効果音は付きません。'}
                  </p>
                  <div style={{ marginTop: 8 }}>
                    <MotionQualitySelect route="brandCut" testId="brand-cut-motion-quality" />
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <MotionModelSelect testId="brand-cut-motion-model" />
                  </div>
                  {colorFallback && (
                    <p className={styles.fieldHint} data-testid="brand-cut-color-fallback" style={{ color: '#e8cd77' }}>
                      {BRAND_COLOR_FALLBACK_NOTICE}
                    </p>
                  )}
                  {motionRateMissing && (
                    <p className={styles.validationWarning}>モーショングラフィックスの料金が設定されていないため、現在ご利用いただけません</p>
                  )}
                </div>
              ) : (
                /* 動画モデル設定（スタジオの UI を移植・ロゴ=参照画像モード） */
                <div className={styles.field}>
                  <h3 className={styles.sectionHeading}>動画生成設定</h3>
                  <VideoGenerationSettings
                    settings={videoSettings}
                    onSettingsChange={setVideoSettings}
                    showResizeMode={false}
                    isReferenceImageMode
                    requireReferenceSupport
                    referenceModeLabel="ロゴ"
                  />
                </div>
              )}
            </div>

            {/* 右カラム: ブランド情報（ブランド名 / スローガン / 説明 / イメージ） */}
            <div className={styles.col}>
              <h3 className={styles.sectionHeading}>ブランド情報</h3>
              <div className={styles.field}>
                <label className={styles.fieldLabel} htmlFor="bc-name">ブランド名<span className={styles.required}>*</span></label>
                <input
                  id="bc-name"
                  className={`${styles.input} ${styles.inputShort}`}
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  placeholder="ブランド名"
                />
              </div>

              <div className={styles.field}>
                <label className={styles.fieldLabel} htmlFor="bc-tagline">スローガン</label>
                <input
                  id="bc-tagline"
                  className={`${styles.input} ${styles.inputMedium}`}
                  value={taglineInput}
                  onChange={(e) => setTaglineInput(e.target.value)}
                  placeholder="ブランドのスローガン（任意）"
                />
              </div>

              <div className={styles.field}>
                <label className={styles.fieldLabel} htmlFor="bc-description">ブランドの説明</label>
                <textarea
                  id="bc-description"
                  className={styles.textarea}
                  value={descriptionInput}
                  onChange={(e) => setDescriptionInput(e.target.value)}
                  placeholder="ブランドの説明（任意）"
                />
              </div>

              <div className={styles.field}>
                <label className={styles.fieldLabel} htmlFor="bc-image">{engine === 'motion' ? '演出の希望' : 'イメージ'}</label>
                <textarea
                  id="bc-image"
                  className={styles.textarea}
                  value={imageText}
                  onChange={(e) => setImageText(e.target.value)}
                  placeholder="演出の希望（例: 高級感のある黒背景でロゴが光りながら現れる など）"
                />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className="btn-modal btn-modal-secondary"
            onClick={onClose}
          >
            キャンセル
          </button>
          <div className={styles.footerActions}>
            {selectedLogoIds.length === 0 && (
              <span className={styles.validationWarning}>
                ロゴ画像を選択してください
              </span>
            )}
            <GenerateButton
              label="生成する"
              creditCost={creditCost}
              onClick={handleSubmit}
              disabled={selectedLogoIds.length === 0 || motionRateMissing}
            />
          </div>
        </div>
      </div>
    </div>
    {/* ロゴ未登録時の新規登録モーダル。登録後はクエリ invalidate で logos が反映される */}
    <LogoManagementModal
      isOpen={logoModalOpen}
      onClose={() => setLogoModalOpen(false)}
      brandId={brandId}
    />
    </>,
    document.body
  );
}
